import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Container,
  Typography,
  Paper,
  IconButton,
  Stack,
  Chip,
  Button,
  ButtonGroup,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack,
  ChevronLeft,
  ChevronRight,
  Today,
  EventNote,
  AccessTime,
  Notifications,
  Warning,
  FiberManualRecord,
  Close,
  CalendarViewMonth,
  ViewWeek,
  Settings,
} from '@mui/icons-material';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  isSameMonth,
  isSameDay,
  isToday,
  parseISO,
  getHours,
  getMinutes,
} from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuthStore } from '../../stores/authStore';
import { apiRequest } from '../../utils/api';
import { MobileBottomNav } from '../common/MobileBottomNav';

const VIEW_MONTH = 'month';
const VIEW_WEEK = 'week';

const EVENT_COLORS = {
  reminder: { bg: '#FEF3C7', border: '#F59E0B', text: '#92400E' },
  appointment: { bg: '#DBEAFE', border: '#2563EB', text: '#1E40AF' },
  notification: { bg: '#D1FAE5', border: '#10B981', text: '#065F46' },
  alert: { bg: '#FEE2E2', border: '#EF4444', text: '#991B1B' },
};

const EVENT_ICONS = {
  reminder: <AccessTime sx={{ fontSize: 14 }} />,
  appointment: <EventNote sx={{ fontSize: 14 }} />,
  notification: <Notifications sx={{ fontSize: 14 }} />,
  alert: <Warning sx={{ fontSize: 14 }} />,
};

const EVENT_LABELS = {
  reminder: 'Recordatorio',
  appointment: 'Cita',
  notification: 'Notificación',
  alert: 'Alerta',
};

const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const WEEKDAYS_FULL = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

export default function CalendarView() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuthStore();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState(VIEW_MONTH);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [filterType, setFilterType] = useState('all');

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      let start, end;

      if (view === VIEW_MONTH) {
        const monthStart = startOfMonth(currentDate);
        const monthEnd = endOfMonth(currentDate);
        start = startOfWeek(monthStart, { weekStartsOn: 1 });
        end = endOfWeek(monthEnd, { weekStartsOn: 1 });
      } else {
        start = startOfWeek(currentDate, { weekStartsOn: 1 });
        end = endOfWeek(currentDate, { weekStartsOn: 1 });
      }

      const fromStr = start.toISOString();
      const toStr = end.toISOString();

      const data = await apiRequest(
        `/notifications/calendar?from=${encodeURIComponent(fromStr)}&to=${encodeURIComponent(toStr)}`
      );

      const parsed = [];

      // Transform notifications
      (data.notifications || []).forEach(n => {
        parsed.push({
          id: n.id,
          title: n.title,
          description: n.body,
          type: n.type === 'reminder' ? 'reminder' : n.type === 'alert' ? 'alert' : 'notification',
          status: n.is_read ? 'leído' : 'no leído',
          start_time: parseISO(n.scheduled_for || n.created_at),
          end_time: null,
          source: n.source,
        });
      });

      // Transform reminders
      (data.reminders || []).forEach(r => {
        parsed.push({
          id: r.id,
          title: r.content,
          description: r.content,
          type: 'reminder',
          status: r.status,
          start_time: parseISO(r.remind_at),
          end_time: null,
          source: r.created_by_role,
          recurrence: r.recurrence,
        });
      });

      // Transform appointments
      (data.appointments || []).forEach(a => {
        const startTime = parseISO(a.scheduled_at);
        const endTime = new Date(startTime.getTime() + (a.duration_minutes || 30) * 60000);
        parsed.push({
          id: a.id,
          title: a.reason || 'Cita médica',
          description: a.reason,
          type: 'appointment',
          status: a.status,
          start_time: startTime,
          end_time: endTime,
          source: 'doctor',
        });
      });

      setEvents(parsed);
    } catch (err) {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [currentDate, view]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const filteredEvents = useMemo(() => {
    if (filterType === 'all') return events;
    return events.filter(e => e.type === filterType);
  }, [events, filterType]);

  const eventsForDay = useCallback(
    (day) => filteredEvents.filter(e => isSameDay(e.start_time, day)),
    [filteredEvents]
  );

  // Navigation
  const goNext = () => {
    setCurrentDate(prev => (view === VIEW_MONTH ? addMonths(prev, 1) : addWeeks(prev, 1)));
  };
  const goPrev = () => {
    setCurrentDate(prev => (view === VIEW_MONTH ? subMonths(prev, 1) : subWeeks(prev, 1)));
  };
  const goToday = () => setCurrentDate(new Date());

  // Build calendar grid
  const calendarDays = useMemo(() => {
    if (view === VIEW_MONTH) {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      const start = startOfWeek(monthStart, { weekStartsOn: 1 });
      const end = endOfWeek(monthEnd, { weekStartsOn: 1 });
      const days = [];
      let day = start;
      while (day <= end) {
        days.push(day);
        day = addDays(day, 1);
      }
      return days;
    } else {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      const days = [];
      for (let i = 0; i < 7; i++) {
        days.push(addDays(start, i));
      }
      return days;
    }
  }, [currentDate, view]);

  const headerTitle = useMemo(() => {
    if (view === VIEW_MONTH) {
      return format(currentDate, 'MMMM yyyy', { locale: es });
    }
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
    if (weekStart.getMonth() === weekEnd.getMonth()) {
      return `${format(weekStart, 'd')} - ${format(weekEnd, 'd MMM yyyy', { locale: es })}`;
    }
    return `${format(weekStart, 'd MMM', { locale: es })} - ${format(weekEnd, 'd MMM yyyy', { locale: es })}`;
  }, [currentDate, view]);

  // Count events by type
  const eventCounts = useMemo(() => {
    const counts = { reminder: 0, appointment: 0, notification: 0, alert: 0 };
    events.forEach(e => {
      if (counts[e.type] !== undefined) counts[e.type]++;
    });
    return counts;
  }, [events]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: { xs: 10, md: 4 } }}>
      <Container maxWidth="lg" sx={{ pt: { xs: 2, md: 4 } }}>
        {/* Top Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: 'background.paper', boxShadow: 1 }}>
                <ArrowBack />
              </IconButton>
              <Box>
                <Typography variant="h2">Calendario</Typography>
                <Typography variant="body2" color="text.secondary">
                  Recordatorios, citas y notificaciones
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={() => navigate('/notifications/settings')} sx={{ bgcolor: 'background.paper', boxShadow: 1 }}>
              <Settings />
            </IconButton>
          </Stack>
        </motion.div>

        {/* Controls Bar */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Paper sx={{ p: 1.5, mb: 2 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
              spacing={1.5}
            >
              {/* Navigation */}
              <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton onClick={goPrev} size="small"><ChevronLeft /></IconButton>
                <IconButton onClick={goNext} size="small"><ChevronRight /></IconButton>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Today />}
                  onClick={goToday}
                  sx={{ minWidth: 'auto', px: 1.5 }}
                >
                  Hoy
                </Button>
                <Typography
                  variant="h4"
                  sx={{ textTransform: 'capitalize', ml: 1, whiteSpace: 'nowrap' }}
                >
                  {headerTitle}
                </Typography>
              </Stack>

              {/* View Toggle */}
              <Stack direction="row" spacing={1}>
                <ButtonGroup size="small" variant="outlined">
                  <Button
                    variant={view === VIEW_MONTH ? 'contained' : 'outlined'}
                    onClick={() => setView(VIEW_MONTH)}
                    startIcon={!isMobile ? <CalendarViewMonth /> : undefined}
                  >
                    {isMobile ? <CalendarViewMonth /> : 'Mes'}
                  </Button>
                  <Button
                    variant={view === VIEW_WEEK ? 'contained' : 'outlined'}
                    onClick={() => setView(VIEW_WEEK)}
                    startIcon={!isMobile ? <ViewWeek /> : undefined}
                  >
                    {isMobile ? <ViewWeek /> : 'Semana'}
                  </Button>
                </ButtonGroup>
              </Stack>
            </Stack>
          </Paper>
        </motion.div>

        {/* Filter Chips */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 2, overflowX: 'auto', pb: 0.5 }} flexWrap="nowrap">
            <Chip
              label={`Todos (${events.length})`}
              onClick={() => setFilterType('all')}
              variant={filterType === 'all' ? 'filled' : 'outlined'}
              color={filterType === 'all' ? 'primary' : 'default'}
              size="small"
            />
            {Object.entries(EVENT_LABELS).map(([type, label]) => (
              <Chip
                key={type}
                icon={<FiberManualRecord sx={{ fontSize: '10px !important', color: `${EVENT_COLORS[type].border} !important` }} />}
                label={`${label} (${eventCounts[type]})`}
                onClick={() => setFilterType(type)}
                variant={filterType === type ? 'filled' : 'outlined'}
                size="small"
                sx={{
                  whiteSpace: 'nowrap',
                  ...(filterType === type && {
                    bgcolor: EVENT_COLORS[type].bg,
                    borderColor: EVENT_COLORS[type].border,
                    color: EVENT_COLORS[type].text,
                  }),
                }}
              />
            ))}
          </Stack>
        </motion.div>

        {/* Calendar Grid */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : view === VIEW_MONTH ? (
            <MonthView
              days={calendarDays}
              currentDate={currentDate}
              eventsForDay={eventsForDay}
              onDayClick={setSelectedDay}
              onEventClick={setSelectedEvent}
              isMobile={isMobile}
            />
          ) : (
            <WeekView
              days={calendarDays}
              eventsForDay={eventsForDay}
              onDayClick={setSelectedDay}
              onEventClick={setSelectedEvent}
              isMobile={isMobile}
            />
          )}
        </motion.div>
      </Container>

      {/* Day Detail Dialog */}
      <DayDetailDialog
        day={selectedDay}
        events={selectedDay ? eventsForDay(selectedDay) : []}
        onClose={() => setSelectedDay(null)}
        onEventClick={(e) => { setSelectedDay(null); setSelectedEvent(e); }}
      />

      {/* Event Detail Dialog */}
      <EventDetailDialog
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
      />

      <MobileBottomNav />
    </Box>
  );
}

// ─── Month View ──────────────────────────────────────────

function MonthView({ days, currentDate, eventsForDay, onDayClick, onEventClick, isMobile }) {
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      {/* Weekday Headers */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          bgcolor: '#F8FAFC',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {(isMobile ? WEEKDAYS : WEEKDAYS_FULL).map((day) => (
          <Box key={day} sx={{ py: 1, textAlign: 'center' }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              {day}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Day Cells */}
      {weeks.map((week, wIdx) => (
        <Box
          key={wIdx}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            borderBottom: wIdx < weeks.length - 1 ? '1px solid' : 'none',
            borderColor: 'divider',
          }}
        >
          {week.map((day, dIdx) => {
            const dayEvents = eventsForDay(day);
            const inMonth = isSameMonth(day, currentDate);
            const today = isToday(day);
            const maxVisible = isMobile ? 2 : 3;

            return (
              <Box
                key={dIdx}
                onClick={() => dayEvents.length > 0 && onDayClick(day)}
                sx={{
                  minHeight: { xs: 60, sm: 90, md: 110 },
                  p: 0.5,
                  borderRight: dIdx < 6 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  cursor: dayEvents.length > 0 ? 'pointer' : 'default',
                  bgcolor: today ? 'rgba(37, 99, 235, 0.04)' : 'transparent',
                  opacity: inMonth ? 1 : 0.4,
                  transition: 'background-color 0.15s',
                  '&:hover': dayEvents.length > 0 ? { bgcolor: 'rgba(37, 99, 235, 0.08)' } : {},
                }}
              >
                {/* Day Number */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.25 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      width: 26,
                      height: 26,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      fontWeight: today ? 700 : 400,
                      color: today ? '#fff' : inMonth ? 'text.primary' : 'text.disabled',
                      bgcolor: today ? '#2563EB' : 'transparent',
                      fontSize: '0.8rem',
                    }}
                  >
                    {format(day, 'd')}
                  </Typography>
                </Box>

                {/* Events */}
                {dayEvents.slice(0, maxVisible).map((event, eIdx) => (
                  <EventChip
                    key={eIdx}
                    event={event}
                    compact={isMobile}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEventClick(event);
                    }}
                  />
                ))}
                {dayEvents.length > maxVisible && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      textAlign: 'center',
                      color: 'primary.main',
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDayClick(day);
                    }}
                  >
                    +{dayEvents.length - maxVisible} más
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      ))}
    </Paper>
  );
}

// ─── Week View ───────────────────────────────────────────

function WeekView({ days, eventsForDay, onDayClick, onEventClick, isMobile }) {
  const hours = Array.from({ length: 16 }, (_, i) => i + 7); // 7:00 to 22:00

  return (
    <Paper sx={{ overflow: 'auto' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `60px repeat(7, 1fr)`,
          bgcolor: '#F8FAFC',
          borderBottom: '2px solid',
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          zIndex: 2,
        }}
      >
        <Box sx={{ py: 1, borderRight: '1px solid', borderColor: 'divider' }} />
        {days.map((day, i) => {
          const today = isToday(day);
          return (
            <Box
              key={i}
              sx={{
                py: 1,
                textAlign: 'center',
                borderRight: i < 6 ? '1px solid' : 'none',
                borderColor: 'divider',
                bgcolor: today ? 'rgba(37, 99, 235, 0.06)' : 'transparent',
              }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {isMobile ? WEEKDAYS[i] : WEEKDAYS_FULL[i]}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  mx: 'auto',
                  color: today ? '#fff' : 'text.primary',
                  bgcolor: today ? '#2563EB' : 'transparent',
                }}
              >
                {format(day, 'd')}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Time Grid */}
      {hours.map((hour) => (
        <Box
          key={hour}
          sx={{
            display: 'grid',
            gridTemplateColumns: `60px repeat(7, 1fr)`,
            borderBottom: '1px solid',
            borderColor: 'divider',
            minHeight: 48,
          }}
        >
          {/* Time Label */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-end',
              pr: 1,
              pt: 0.25,
              borderRight: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              {`${hour.toString().padStart(2, '0')}:00`}
            </Typography>
          </Box>

          {/* Day Columns */}
          {days.map((day, dIdx) => {
            const dayEvents = eventsForDay(day).filter(e => {
              const eventHour = getHours(e.start_time);
              return eventHour === hour;
            });

            return (
              <Box
                key={dIdx}
                sx={{
                  position: 'relative',
                  borderRight: dIdx < 6 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  bgcolor: isToday(day) ? 'rgba(37, 99, 235, 0.02)' : 'transparent',
                  p: 0.25,
                }}
              >
                {dayEvents.map((event, eIdx) => (
                  <EventChip
                    key={eIdx}
                    event={event}
                    showTime
                    compact={isMobile}
                    onClick={() => onEventClick(event)}
                  />
                ))}
              </Box>
            );
          })}
        </Box>
      ))}
    </Paper>
  );
}

// ─── Event Chip ──────────────────────────────────────────

function EventChip({ event, compact = false, showTime = false, onClick }) {
  const colors = EVENT_COLORS[event.type] || EVENT_COLORS.notification;
  const timeStr = `${getHours(event.start_time).toString().padStart(2, '0')}:${getMinutes(event.start_time).toString().padStart(2, '0')}`;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 0.5,
        py: 0.25,
        mb: 0.25,
        borderRadius: '4px',
        bgcolor: colors.bg,
        borderLeft: `3px solid ${colors.border}`,
        cursor: 'pointer',
        overflow: 'hidden',
        transition: 'transform 0.1s, box-shadow 0.1s',
        '&:hover': {
          transform: 'scale(1.02)',
          boxShadow: `0 1px 4px ${colors.border}40`,
        },
      }}
    >
      {!compact && EVENT_ICONS[event.type]}
      <Typography
        variant="caption"
        sx={{
          fontSize: compact ? '0.6rem' : '0.7rem',
          fontWeight: 600,
          color: colors.text,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          lineHeight: 1.3,
        }}
      >
        {showTime && `${timeStr} `}
        {event.title}
      </Typography>
    </Box>
  );
}

// ─── Day Detail Dialog ───────────────────────────────────

function DayDetailDialog({ day, events, onClose, onEventClick }) {
  if (!day) return null;

  return (
    <Dialog open={!!day} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h3" sx={{ textTransform: 'capitalize' }}>
          {format(day, "EEEE d 'de' MMMM", { locale: es })}
        </Typography>
        <IconButton onClick={onClose} size="small"><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {events.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            No hay eventos este día
          </Typography>
        ) : (
          <Stack spacing={1}>
            {events.map((event, idx) => {
              const colors = EVENT_COLORS[event.type] || EVENT_COLORS.notification;
              const timeStr = format(event.start_time, 'HH:mm');
              return (
                <Paper
                  key={idx}
                  variant="outlined"
                  onClick={() => onEventClick(event)}
                  sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    borderLeft: `4px solid ${colors.border}`,
                    '&:hover': { bgcolor: colors.bg },
                    transition: 'background-color 0.15s',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {EVENT_ICONS[event.type]}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" fontWeight={600}>{event.title}</Typography>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="caption" color="text.secondary">{timeStr}</Typography>
                        <Chip
                          label={EVENT_LABELS[event.type]}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            bgcolor: colors.bg,
                            color: colors.text,
                            borderColor: colors.border,
                          }}
                          variant="outlined"
                        />
                        {event.status && (
                          <Chip
                            label={event.status}
                            size="small"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                            variant="outlined"
                          />
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Event Detail Dialog ─────────────────────────────────

function EventDetailDialog({ event, onClose }) {
  if (!event) return null;
  const colors = EVENT_COLORS[event.type] || EVENT_COLORS.notification;

  return (
    <Dialog open={!!event} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          bgcolor: colors.bg,
          borderBottom: `3px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {EVENT_ICONS[event.type]}
          <Typography variant="h4" sx={{ color: colors.text }}>
            {EVENT_LABELS[event.type]}
          </Typography>
        </Stack>
        <IconButton onClick={onClose} size="small"><Close /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="h3" gutterBottom>{event.title}</Typography>

        {event.description && event.description !== event.title && (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {event.description}
          </Typography>
        )}

        <Divider sx={{ my: 1.5 }} />

        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AccessTime sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2">
              {format(event.start_time, "EEEE d 'de' MMMM, yyyy", { locale: es })}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AccessTime sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2">
              {format(event.start_time, 'HH:mm')}
              {event.end_time && ` - ${format(event.end_time, 'HH:mm')}`}
            </Typography>
          </Stack>
          {event.status && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <FiberManualRecord sx={{ fontSize: 12, color: colors.border }} />
              <Typography variant="body2">
                Estado: <strong>{event.status}</strong>
              </Typography>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
